import { useCallback, useMemo, useState } from "react"

import SelectableVariantsTable from "~/components/collections/SelectableVariantsTable"
import { Button } from "~/components/reusable/Button"
import {
  DialogClose,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogBodyV2,
  DialogTitle,
} from "~/components/reusable/Dialog"
import { useVariants } from "~/hooks/useVariants"
import { formatCount } from "~/utils/formatting"

type Props = {
  onAddVariants: (variantIds: string[]) => void
  variantIdsInUse?: string[]
  isLoading?: boolean
  isNoosCollection?: boolean
  suggestedVariantIds?: string[]
  /** Where the grid persists its columns; Demand 2 passes its own so legacy layouts stay out. */
  tableId?: string
}
export default function AddVariantsContent({
  onAddVariants,
  variantIdsInUse,
  isLoading,
  isNoosCollection,
  suggestedVariantIds,
  tableId = "add-variants-table",
}: Props) {
  const [selectedVariants, setSelectedVariants] = useState<string[]>([])
  const allVariants = useVariants()

  const variants = useMemo(() => {
    let filtered = allVariants?.filter((s) => !variantIdsInUse?.includes(s.variantId)) ?? []
    if (isNoosCollection) {
      filtered = filtered.filter((s) => s.productType === "noos")
    }
    return filtered
  }, [allVariants, variantIdsInUse, isNoosCollection])

  // Most suggested variants are hidden until they join a collection, so they can't come from the
  // client store; the server resolves their SKUs.
  const suggestedToAdd = useMemo(() => {
    const inUse = new Set(variantIdsInUse)
    return (suggestedVariantIds ?? []).filter((id) => !inUse.has(id))
  }, [suggestedVariantIds, variantIdsInUse])

  const handleSelectedVariantsChange = useCallback((variantIds: string[]) => {
    setSelectedVariants(variantIds)
  }, [])

  const handleSubmit = () => {
    onAddVariants(selectedVariants)
  }

  return (
    <DialogContentV2 className="min-w-[90vw]">
      <DialogHeaderV2>
        <DialogTitle>Add multiple variants</DialogTitle>
      </DialogHeaderV2>
      <DialogBodyV2>
        <SelectableVariantsTable
          id={tableId}
          variants={variants}
          updateSelectedVariants={handleSelectedVariantsChange}
        />
      </DialogBodyV2>
      <DialogFooterV2 className="justify-end gap-2">
        {suggestedToAdd.length > 0 && (
          <Button
            variant="neutral"
            requires="edit"
            onClick={() => onAddVariants(suggestedToAdd)}
            loading={isLoading}
          >
            {`Add ${formatCount(suggestedToAdd.length, "variant")} with sales`}
          </Button>
        )}
        <DialogClose>
          <Button asChild variant="transparent">
            Cancel
          </Button>
        </DialogClose>
        <Button onClick={handleSubmit} loading={isLoading} disabled={selectedVariants.length === 0}>
          {isLoading
            ? `Adding ${selectedVariants.length} variant${selectedVariants.length === 1 ? "" : "s"}...`
            : `Add ${selectedVariants.length} variant${selectedVariants.length === 1 ? "" : "s"}`}
        </Button>
      </DialogFooterV2>
    </DialogContentV2>
  )
}
